import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { AlertTriangle, Info, Loader2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";
import { Button } from "@/shared/components/ui/button";
import { type ToolOption } from "@/shared/components/ui/tool-select";
import {
  ModelPurposeAccordion,
  type PurposeLabels,
} from "@/shared/components/ai/ModelPurposeAccordion";
import { FallbackChain } from "@/shared/components/ai/FallbackChain";
import {
  aprovado,
  nivelVazio,
  type FieldCheck,
  type NivelDaCadeia,
  type PurposeChoice,
} from "@/shared/types/aiModels";
import { isModelless, readModelCatalog, type ModelPurpose } from "@/shared/utils/modelCatalog";
import {
  PlatformAiService,
  type PlatformAiFallbackPayload,
  type PlatformAiModelsPayload,
} from "@/features/PlatformSettings/services/platformAiService";

/**
 * Modelos de IA da plataforma (AB#85605).
 *
 * Quatro finalidades, cada uma com provedor e modelo próprios: conversação (preenchimento
 * assistido, expansão de busca, overview mágico, tabular), indexação, transcrição das
 * reuniões e busca web (AB#86885) — esta só para o pago: o provedor gratuito embutido no
 * engine é sempre tentado antes, e o escolhido aqui entra quando ele falha. Nada aqui é texto livre — o provedor vem do cadastro de integrações da
 * organização da plataforma, e o modelo, do catálogo que esse cadastro declara.
 *
 * O Save espera o veredito do teste de cada finalidade preenchida: gravar um modelo que não
 * responde é como a transcrição ficou muda por dez dias sem ninguém perceber.
 */

const FINALIDADES: ModelPurpose[] = ["conversational", "embedding", "transcription", "web_search"];

/**
 * `promptContext` só tem sentido na transcrição, e é dela que vem: é o texto fixo anexado a
 * todo prompt enviado ao modelo de áudio. Fica na mesma estrutura das outras finalidades
 * porque é característica DA ESCOLHA — trocar de provedor mantendo o contexto é o caso comum,
 * e é por isso que o cadastro o guarda em `organization_ai_models.options`.
 */
type Escolha = { integrationId: string | null; model: string; promptContext: string };

const VAZIA: Escolha = { integrationId: null, model: "", promptContext: "" };

/**
 * O `prompt_context` que roda em produção, oferecido como exemplo e não como valor.
 *
 * Exemplo em vez de valor inicial de propósito: preencher o campo sozinho gravaria esse texto
 * na primeira vez que alguém salvasse a tela por outro motivo, e passaria por escolha de quem
 * configurou. Vive no diálogo de ajuda, não no campo: é longo demais para placeholder.
 *
 * A primeira frase é o FILTRO ÉTICO (AB#82130): ela instrui o modelo de áudio a trocar
 * palavrão, ofensa e discurso de ódio pelo marcador em vez de transcrever ao pé da letra,
 * para que a transcrição não nasça carregando esse conteúdo até os resumos, a base de
 * conhecimento e os webhooks. Ela veio por migration e não pertence a nenhum template de
 * provedor — sem estar à vista aqui, um ambiente configurado do zero a perderia em silêncio.
 * É mitigação best-effort no nível do modelo, não filtro determinístico.
 *
 * O fim pede UM bloco do overview (`vocabulary`), não o overview inteiro: medido, o overview
 * completo resolvia para 8.274 caracteres e 1.548 tokens de entrada por chamada — contra 28
 * de saída —, e o modelo ainda assim escrevia "Progy" no lugar de "Prodgy". Com o bloco, o
 * contexto caiu para 743 caracteres e o nome passou a sair certo.
 */
const EXEMPLO_PROMPT_CONTEXT =
  "Apply appropriate ethical filtering: when speech contains profanity, slurs, hate speech, " +
  "insults or offensive/discriminatory language, do not transcribe the offending term " +
  "literally; instead replace each offending term with the marker <ethical_filter_applied> " +
  "while keeping the rest of the sentence accurate and intact. Transcribe all other speech " +
  "faithfully. Solution: Prodgy - A AI Brain to orchestrate AI Agents and supply then with " +
  "enterprise knowledge. Company: ${organization.name}, ${organization.description}. " +
  "Workspace: ${product.name}, ${product.description}. Vocabulary: ${product.overview.vocabulary}.";

/**
 * Um ícone por finalidade: as três seções têm a mesma forma, e o rótulo é a única coisa que
 * as distingue quando todas estão fechadas.
 */
/**
 * Os placeholders que o `prompt_context` aceita, resolvidos contra organização e workspace no
 * momento da transcrição. A lista é do template, não da tela — quem a resolve é
 * `resolvePromptContext`, no serviço de transcrição.
 */
const PLACEHOLDERS = [
  "organization.name",
  "organization.description",
  "product.name",
  "product.description",
  "product.overview.vocabulary",
] as const;

/**
 * O logo da integração escolhida, ao lado do modelo.
 *
 * Some sem deixar buraco quando o cadastro não tem logo ou a imagem não carrega — o resumo
 * continua legível pelo nome do modelo, que é a informação que não pode faltar.
 */
/** `options.prompt_context` do que o cadastro devolve — jsonb livre, então nada é presumido. */
function lerPromptContext(options: Record<string, unknown> | undefined): string {
  const valor = options?.prompt_context;
  return typeof valor === "string" ? valor : "";
}

/**
 * Catálogo e veredito de UMA finalidade, isolados das outras duas.
 *
 * Um efeito por finalidade, dependendo apenas dos escalares dela. Com um efeito só varrendo as
 * três, trocar o modelo da conversação refazia o teste do embedding e da transcrição: o
 * dependente era o objeto agregado, que ganha identidade nova quando qualquer uma das três
 * muda. O custo não era só desperdício — o veredito das outras duas voltava para `checking` e
 * o Save ficava bloqueado esperando prova de algo que ninguém tinha mexido, a cada tecla.
 *
 * São chamadas fixas, uma por finalidade, e nesta ordem, nunca em laço nem sob condição: a ordem dos hooks
 * precisa ser a mesma em toda renderização.
 */
function useFinalidade(
  purpose: ModelPurpose,
  escolha: Escolha,
  carregarCatalogo: (
    purpose: ModelPurpose,
    integrationId: string | null,
    cancelado: () => boolean
  ) => void,
  checar: (
    purpose: ModelPurpose,
    integrationId: string | null,
    model: string,
    cancelado: () => boolean
  ) => Promise<void>
) {
  const { integrationId, model } = escolha;

  // O catálogo depende só da integração — o modelo muda por dentro dela.
  useEffect(() => {
    let cancelado = false;
    carregarCatalogo(purpose, integrationId, () => cancelado);
    return () => {
      cancelado = true;
    };
  }, [purpose, integrationId, carregarCatalogo]);

  useEffect(() => {
    let cancelado = false;
    void checar(purpose, integrationId, model, () => cancelado);
    return () => {
      cancelado = true;
    };
  }, [purpose, integrationId, model, checar]);
}

export const PlatformAiModelsSettings = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [escolhas, setEscolhas] = useState<Record<ModelPurpose, Escolha>>({
    conversational: VAZIA,
    embedding: VAZIA,
    transcription: VAZIA,
    web_search: VAZIA,
  });
  const [iniciais, setIniciais] = useState<Record<ModelPurpose, Escolha>>({
    conversational: VAZIA,
    embedding: VAZIA,
    transcription: VAZIA,
    web_search: VAZIA,
  });
  const [catalogos, setCatalogos] = useState<Record<ModelPurpose, string[]>>({
    conversational: [],
    embedding: [],
    transcription: [],
    web_search: [],
  });
  const [checks, setChecks] = useState<Record<ModelPurpose, FieldCheck>>({
    conversational: { state: "idle" },
    embedding: { state: "idle" },
    transcription: { state: "idle" },
    web_search: { state: "idle" },
  });
  const [ajudaAberta, setAjudaAberta] = useState(false);
  /**
   * Abre o que pede trabalho: finalidade ainda não configurada. O que já responde fica
   * fechado, resumido no cabeçalho — abrir tudo seria a lista corrida de volta.
   */
  const [secoesAbertas, setSecoesAbertas] = useState<string[]>([]);

  /**
   * Os NÍVEIS da cadeia de contingência, na ordem de tentativa (AB#85617).
   *
   * Lista, e não um elo só: `priority` sempre descreveu uma cadeia, e o gateway percorre
   * quantos alvos receber. Cada nível traz as três finalidades; deixar uma em branco não
   * acrescenta elo à cadeia dela, que é como os comprimentos seguem independentes.
   *
   * Estado separado das principais, e não um nível dentro do mesmo mapa: os dois blocos
   * gravam por rotas diferentes, e misturá-los faria um salvamento arrastar o outro.
   */
  const [niveisContingencia, setNiveisContingencia] = useState<
    Array<NivelDaCadeia<ModelPurpose>>
  >([]);
  const [niveisInicial, setNiveisInicial] = useState<Array<NivelDaCadeia<ModelPurpose>>>([]);
  const [contingenciaPendente, setContingenciaPendente] = useState(false);

  const { data: gravado, isLoading } = useQuery({
    queryKey: ["platform-ai-models"],
    queryFn: () => PlatformAiService.getModels(),
  });

  const platformOrgId = gravado?.organization_id ?? null;

  useEffect(() => {
    if (!gravado) return;
    const lido: Record<ModelPurpose, Escolha> = {
      conversational: {
        integrationId: gravado.conversational?.integration_base_id ?? null,
        model: gravado.conversational?.model ?? "",
        promptContext: "",
      },
      embedding: {
        integrationId: gravado.embedding?.integration_base_id ?? null,
        model: gravado.embedding?.model ?? "",
        promptContext: "",
      },
      transcription: {
        integrationId: gravado.transcription?.integration_base_id ?? null,
        model: gravado.transcription?.model ?? "",
        promptContext: lerPromptContext(gravado.transcription?.options),
      },
      web_search: {
        integrationId: gravado.web_search?.integration_base_id ?? null,
        model: gravado.web_search?.model ?? "",
        promptContext: "",
      },
    };
    setEscolhas(lido);
    setIniciais(lido);
    // Só o que falta configurar nasce aberto. Depois disso quem manda é o clique: refazer a
    // abertura a cada resposta do servidor fecharia a seção em que a pessoa está digitando.
    setSecoesAbertas(FINALIDADES.filter((purpose) => !lido[purpose].model));

    // A cadeia de contingência vem no mesmo payload, num ramo à parte (AB#85617).
    //
    // O dado é uma cadeia POR FINALIDADE, de comprimentos independentes; a tela mostra
    // NÍVEIS. O nível N reúne o N-ésimo elo de cada finalidade, e o número de níveis é o da
    // cadeia mais longa — finalidade mais curta fica em branco nos níveis excedentes.
    //
    // O `prompt_context` NÃO é lido aqui: ele descreve a transcrição da plataforma, e é da
    // escolha principal — duplicá-lo num elo criaria dois textos para o mesmo propósito.
    const cadeias = gravado.fallbacks ?? {
      conversational: [],
      embedding: [],
      transcription: [],
      web_search: [],
    };
    const profundidade = Math.max(
      ...FINALIDADES.map((purpose) => cadeias[purpose]?.length ?? 0)
    );
    const lidos = Array.from({ length: profundidade }, (_, nivel) =>
      nivelVazio(
        FINALIDADES,
        FINALIDADES.reduce(
          (acc, purpose) => ({
            ...acc,
            [purpose]: {
              integrationId: cadeias[purpose]?.[nivel]?.integration_base_id ?? null,
              model: cadeias[purpose]?.[nivel]?.model ?? "",
            },
          }),
          {} as Record<ModelPurpose, PurposeChoice>
        )
      )
    );

    setNiveisContingencia(lidos);
    setNiveisInicial(lidos);
  }, [gravado]);

  /**
   * Os provedores da PLATAFORMA, não os da organização ativa.
   *
   * O superadmin abre esta tela em escopo de plataforma, sem organização ativa; sem o
   * escopo explícito a listagem cairia na organização do perfil dele.
   */
  const { data: providers, isLoading: isLoadingProviders } = useQuery<ToolOption[]>({
    queryKey: ["platform-llm-integrations", platformOrgId, i18n.language],
    queryFn: async (): Promise<ToolOption[]> => {
      if (!platformOrgId) return [];
      const bases = await IntegrationBaseService.getAll({
        classification: "llm",
        status: "active",
        organization_id: platformOrgId,
      });
      return bases.map((integration): ToolOption => ({
        id: integration.id,
        name: integration.label,
        description: getIntegrationDescription(integration, i18n.language) || "",
        logo_url: integration.logo_url || integration.template?.logo_url || null,
      }));
    },
    enabled: !!platformOrgId,
  });

  /**
   * Os provedores da BUSCA WEB: as integrações que declaram `capabilities.web_search`
   * (AB#86885), e não as de LLM — a Tavily não é LLM, e um recurso de LLM sem a tool de
   * busca não serve aqui.
   */
  const { data: webSearchProviders, isLoading: isLoadingWebSearchProviders } = useQuery<
    ToolOption[]
  >({
    queryKey: ["platform-web-search-integrations", platformOrgId, i18n.language],
    queryFn: async (): Promise<ToolOption[]> => {
      if (!platformOrgId) return [];
      const bases = await IntegrationBaseService.getAll({
        capability: "web_search",
        status: "active",
        organization_id: platformOrgId,
      });
      return bases.map((integration): ToolOption => ({
        id: integration.id,
        name: integration.label,
        description: getIntegrationDescription(integration, i18n.language) || "",
        logo_url: integration.logo_url || integration.template?.logo_url || null,
      }));
    },
    enabled: !!platformOrgId,
  });

  const providersByPurpose: Partial<Record<ModelPurpose, ToolOption[]>> = {
    web_search: webSearchProviders || [],
  };

  /**
   * Carrega o catálogo de UMA finalidade, da integração dela.
   *
   * A guarda de cancelamento existe porque trocar de provedor dispara um fetch novo sem
   * cancelar o anterior, e a resposta que chega por último venceria — a tela mostraria os
   * modelos da integração errada.
   */
  const carregarCatalogo = useCallback(
    (purpose: ModelPurpose, integrationId: string | null, cancelado: () => boolean) => {
      if (!integrationId || !platformOrgId) {
        setCatalogos((atual) => ({ ...atual, [purpose]: [] }));
        return;
      }
      IntegrationBaseService.getById(integrationId, platformOrgId)
        .then((integration) => {
          if (cancelado()) return;
          const declarados = readModelCatalog(integration?.metadata)[purpose];
          setCatalogos((atual) => ({ ...atual, [purpose]: declarados }));
          // Modelo que a nova integração não declara não pode continuar selecionado: ele
          // sairia com a credencial dela e falharia no provedor. Provedor sem modelo
          // (Tavily, AB#86885) recebe o marcador sozinho — o campo nem aparece.
          setEscolhas((atual) => {
            const corrente = atual[purpose].model;
            const model = isModelless(declarados)
              ? declarados[0]
              : declarados.includes(corrente)
                ? corrente
                : "";
            return { ...atual, [purpose]: { ...atual[purpose], model } };
          });
        })
        .catch((error) => {
          if (cancelado()) return;
          // Sem o catálogo a finalidade fica sem opções, e um console não explica isso a
          // quem está na tela. A lista vai a vazia e a falha aparece como as demais.
          setCatalogos((atual) => ({ ...atual, [purpose]: [] }));
          toast({
            variant: "destructive",
            title: t("common.error"),
            description: resolveApiErrorMessage(error, t),
          });
        });
    },
    [platformOrgId, toast, t]
  );

  /**
   * Valida UMA finalidade, com a escolha que está na tela, pelo mesmo caminho da chamada de
   * produção. Validar o que está gravado responderia a pergunta errada.
   */
  const checar = useCallback(
    async (
      purpose: ModelPurpose,
      integrationId: string | null,
      model: string,
      cancelado: () => boolean
    ) => {
      if (!integrationId || !model) {
        setChecks((atual) => ({ ...atual, [purpose]: { state: "idle" } }));
        return;
      }

      setChecks((atual) => ({ ...atual, [purpose]: { state: "checking" } }));
      const aplicar = (check: FieldCheck) => {
        if (cancelado()) return;
        setChecks((atual) => ({ ...atual, [purpose]: check }));
      };

      try {
        const report = await PlatformAiService.testModel({
          purpose,
          integration_base_id: integrationId,
          model,
        });
        aplicar(
          report.status === "ok"
            ? { state: "done", outcome: report[purpose] ?? { status: "skipped" } }
            : { state: "unavailable", reason: report.status }
        );
      } catch {
        aplicar({ state: "unreachable" });
      }
    },
    []
  );

  useFinalidade("conversational", escolhas.conversational, carregarCatalogo, checar);
  useFinalidade("embedding", escolhas.embedding, carregarCatalogo, checar);
  useFinalidade("transcription", escolhas.transcription, carregarCatalogo, checar);
  useFinalidade("web_search", escolhas.web_search, carregarCatalogo, checar);

  // A contingência não passa por aqui: cada elo da cadeia cuida do próprio catálogo e da
  // própria sonda, porque o número de elos é dinâmico e `useEffect` não roda em laço.

  const alterar = (purpose: ModelPurpose, patch: Partial<Escolha>) =>
    setEscolhas((atual) => ({ ...atual, [purpose]: { ...atual[purpose], ...patch } }));

  const save = useMutation({
    mutationFn: async () => {
      const payload: PlatformAiModelsPayload = {};
      for (const purpose of FINALIDADES) {
        const escolha = escolhas[purpose];
        // Finalidade esvaziada vira `null`: apaga a linha em vez de gravar um modelo vazio,
        // que não descreveria escolha nenhuma. Sem escolha, a plataforma segue pela
        // configuração anterior.
        const contexto = escolha.promptContext.trim();
        payload[purpose] =
          escolha.integrationId && escolha.model
            ? {
                integration_base_id: escolha.integrationId,
                model: escolha.model,
                // Campo limpo NÃO é campo preservado: o cadastro grava `options` a cada
                // gravação (`options ?? {}` no merge), então omitir aqui apaga o contexto
                // anterior — que é justamente o que limpar o campo deve fazer.
                ...(contexto ? { options: { prompt_context: contexto } } : {}),
              }
            : null;
      }
      await PlatformAiService.updateModels(payload);
    },
    onSuccess: () => {
      setIniciais(escolhas);
      toast({
        title: t("settings.platformModels.updateSuccess"),
        description: t("settings.platformModels.updateSuccessDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ["platform-ai-models"] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
      });
    },
  });

  const mudou = FINALIDADES.some(
    (purpose) =>
      escolhas[purpose].integrationId !== iniciais[purpose].integrationId ||
      escolhas[purpose].model !== iniciais[purpose].model ||
      escolhas[purpose].promptContext !== iniciais[purpose].promptContext
  );

  // Finalidade em branco não bloqueia o Save — deixar de configurar uma delas é legítimo, e
  // nesse caso a plataforma segue pela configuração anterior. O que bloqueia é finalidade
  // preenchida que NÃO passou no teste.
  const pendente = FINALIDADES.some((purpose) => {
    const { integrationId, model } = escolhas[purpose];
    if (!integrationId || !model) return false;
    return !aprovado(checks[purpose]);
  });

  /**
   * Salvar da CONTINGÊNCIA: rota própria, que não toca nas escolhas principais (AB#85617).
   *
   * O `prompt_context` não viaja daqui — ele descreve a transcrição da plataforma e pertence
   * à escolha principal. Mandá-lo no elo de contingência criaria dois textos para o mesmo
   * propósito, e nada diria qual vale.
   */
  const salvarContingencia = useMutation({
    mutationFn: async () => {
      // Os NÍVEIS da tela viram a cadeia de CADA finalidade (AB#85617). Finalidade em
      // branco num nível simplesmente não entra na cadeia dela — é assim que as três
      // cadeias voltam a ter comprimentos independentes.
      const payload: PlatformAiFallbackPayload = {};
      for (const purpose of FINALIDADES) {
        payload[purpose] = niveisContingencia
          .map((nivel) => nivel.escolhas[purpose])
          .filter((elo) => elo.integrationId && elo.model)
          .map((elo) => ({ integration_base_id: elo.integrationId!, model: elo.model }));
      }
      await PlatformAiService.updateFallback(payload);
    },
    onSuccess: () => {
      setNiveisInicial(niveisContingencia);
      toast({
        title: t("settings.platformModels.fallback.updateSuccess"),
        description: t("settings.platformModels.fallback.updateSuccessDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ["platform-ai-models"] });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: resolveApiErrorMessage(error, t),
      });
    },
  });

  /**
   * A cadeia mudou em relação ao que está gravado.
   *
   * Compara por serialização porque a comparação é estrutural e a lista é curta — no
   * máximo três níveis de três campos. Um comparador campo a campo aqui só repetiria isso
   * com mais linhas e mais chance de esquecer um campo novo.
   */
  const contingenciaMudou =
    JSON.stringify(niveisContingencia) !== JSON.stringify(niveisInicial);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("settings.platformModels.title")}</CardTitle>
        <CardDescription>{t("settings.platformModels.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {!platformOrgId ? (
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t("settings.platformModels.missingOrganization")}</span>
          </div>
        ) : (
          <ModelPurposeAccordion
            purposes={FINALIDADES}
            labels={Object.fromEntries(
              FINALIDADES.map((purpose) => [
                purpose,
                {
                  section: t(`settings.platformModels.purpose.${purpose}.section`),
                  help: t(`settings.platformModels.purpose.${purpose}.help`),
                  provider: t(`settings.platformModels.purpose.${purpose}.provider`),
                  model: t(`settings.platformModels.purpose.${purpose}.model`),
                },
              ])
            ) as Record<ModelPurpose, PurposeLabels>}
            values={escolhas}
            checks={checks}
            catalogs={catalogos}
            providers={providers || []}
            providersByPurpose={providersByPurpose}
            loadingProviders={isLoadingProviders || isLoadingWebSearchProviders}
            open={secoesAbertas}
            onOpenChange={setSecoesAbertas}
            onChange={alterar}
            notConfiguredLabel={t("settings.platformModels.notConfigured")}
            selectModelPlaceholder={t("settings.platformModels.selectModel")}
            saving={save.isPending}
            canSave={mudou && !pendente}
            saveLabel={t("common.actions.save")}
            savingLabel={t("common.actions.saving")}
            onSubmit={() => save.mutate()}
            extra={(purpose) =>
              /* Só a transcrição: o texto vai no parâmetro `prompt` da API de áudio, que as
                 outras duas finalidades não têm. Fora do contrato do componente porque é de
                 UMA finalidade de UMA tela — os outros três usos teriam de ignorá-lo. */
              purpose === "transcription" ? (
                <div className="space-y-1.5">
                  <Label htmlFor="platform-prompt-context">
                    {t("settings.platformModels.promptContext.label")}
                  </Label>
                  <Textarea
                    id="platform-prompt-context"
                    value={escolhas.transcription.promptContext}
                    onChange={(e) => alterar("transcription", { promptContext: e.target.value })}
                    placeholder={t("settings.platformModels.promptContext.placeholder")}
                    rows={3}
                    className="font-mono text-xs"
                  />
                  {/* O ícone acompanha a frase que fala em placeholders — é ali que a
                      pergunta "quais?" nasce. */}
                  <div className="flex items-start gap-1 text-xs text-muted-foreground">
                    <span>{t("settings.platformModels.promptContext.help")}</span>
                    <button
                      type="button"
                      onClick={() => setAjudaAberta(true)}
                      aria-label={t("settings.platformModels.promptContext.openHelp")}
                      className="mt-0.5 shrink-0 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ) : null
            }
          />
        )}
      </CardContent>
    </Card>

    {/* O bloco de CONTINGÊNCIA (AB#85617): irmão do de cima, mesmo formato, salvar próprio.
        Quando o principal falha, o gateway repete nele e só então passa para cá. */}
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.platformModels.fallback.title")}</CardTitle>
        <CardDescription>{t("settings.platformModels.fallback.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? null : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              salvarContingencia.mutate();
            }}
            className="space-y-4"
          >
            <FallbackChain
              purposes={FINALIDADES}
              labels={
                Object.fromEntries(
                  FINALIDADES.map((purpose) => [
                    purpose,
                    {
                      section: t(`settings.platformModels.purpose.${purpose}.section`),
                      help: t(`settings.platformModels.purpose.${purpose}.help`),
                      provider: t(`settings.platformModels.purpose.${purpose}.provider`),
                      model: t(`settings.platformModels.purpose.${purpose}.model`),
                    },
                  ])
                ) as Record<ModelPurpose, PurposeLabels>
              }
              niveis={niveisContingencia}
              onNiveisChange={setNiveisContingencia}
              onPendingChange={setContingenciaPendente}
              providers={providers || []}
              providersByPurpose={providersByPurpose}
              loadingProviders={isLoadingProviders || isLoadingWebSearchProviders}
              notConfiguredLabel={t("settings.platformModels.fallback.notConfigured")}
              selectModelPlaceholder={t("settings.platformModels.selectModel")}
              loadCatalog={async (purpose: ModelPurpose, integrationId: string) => {
                if (!platformOrgId) return [];
                const integration = await IntegrationBaseService.getById(
                  integrationId,
                  platformOrgId
                );
                return readModelCatalog(integration?.metadata)[purpose];
              }}
              testModel={async (
                purpose: ModelPurpose,
                integration_base_id: string,
                model: string
              ): Promise<FieldCheck> => {
                try {
                  const report = await PlatformAiService.testModel({
                    purpose,
                    integration_base_id,
                    model,
                  });
                  return report.status === "ok"
                    ? { state: "done", outcome: report[purpose] ?? { status: "skipped" } }
                    : { state: "unavailable", reason: report.status };
                } catch {
                  return { state: "unreachable" };
                }
              }}
              texts={{
                linkTitle: (posicao) =>
                  t("settings.fallbackChain.linkTitle", { position: posicao }),
                addLink: t("settings.fallbackChain.addLink"),
                removeLink: t("settings.fallbackChain.removeLink"),
                hint: t("settings.fallbackChain.hint"),
                empty: t("settings.fallbackChain.empty"),
              }}
            />

            <Button
              type="submit"
              disabled={
                salvarContingencia.isPending || !contingenciaMudou || contingenciaPendente
              }
              className="w-full sm:w-auto"
            >
              {salvarContingencia.isPending
                ? t("common.actions.saving")
                : t("common.actions.save")}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>

      {/* Os placeholders ficam aqui, e não sob o campo: são cinco, e listá-los na tela
          empurrava o Save para fora da primeira dobra. */}
      <Dialog open={ajudaAberta} onOpenChange={setAjudaAberta}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t("settings.platformModels.promptContext.placeholders.title")}
            </DialogTitle>
            <DialogDescription>
              {t("settings.platformModels.promptContext.placeholders.description")}
            </DialogDescription>
          </DialogHeader>

          <ul className="space-y-2">
            {PLACEHOLDERS.map((token) => (
              <li key={token} className="flex flex-col gap-0.5">
                <code className="w-fit rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {"${" + token + "}"}
                </code>
                <span className="text-xs text-muted-foreground">
                  {t(`settings.platformModels.promptContext.placeholders.tokens.${token}`)}
                </span>
              </li>
            ))}
          </ul>

          <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
            {t("settings.platformModels.promptContext.placeholders.note")}
          </p>

          <div className="space-y-1.5 border-t pt-3">
            <p className="text-xs font-medium">
              {t("settings.platformModels.promptContext.placeholders.exampleLabel")}
            </p>
            <p className="scroll-slim max-h-40 overflow-y-auto rounded bg-muted p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {EXEMPLO_PROMPT_CONTEXT}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
